import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { useActiveBusinessStore } from './hooks/useActiveBusiness';
import RequiresActiveBusinessGate from './components/money/RequiresActiveBusinessGate';
import { AppShell } from './components/layout/AppShell';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { LoginPage } from './pages/LoginPage';
import DashboardRouter from './pages/DashboardRouter';
import SetupGuidePage from './pages/SetupGuide';
import { SchedulePage } from './pages/Schedule';
import { TeamPage } from './pages/TeamPage';
import {
  MoneyOverviewAdmin,
  MoneyOutstandingAdmin,
  MoneyActivityPage,
  MoneyExpensesPage,
  MoneyInvoicesPage,
  MoneyRecurringPage,
  MoneyRevenuePage,
  MoneyTaxPage,
  MoneyDisputesPage,
  MoneySettingsLanding,
  MoneySettingsPaid,
  MoneySettingsTaxes,
  MoneySettingsPeople,
  MoneySettingsNotifications,
  MoneySettingsBranding,
} from './pages/Money';
import { ReportsPage } from './pages/ReportsPage';
import { MessagesPage } from './pages/Messages';
import { ModerationPage } from './pages/Moderation';
import PlatformAdminGate from './components/common/PlatformAdminGate';
import { SettingsLayout } from './pages/Settings';
import { SettingsHub } from './pages/Settings/SettingsHub';
import { BusinessProfilePage } from './pages/Settings/pages/BusinessProfilePage';
import { BusinessHoursPage } from './pages/Settings/pages/BusinessHoursPage';
import { TeamRolesPage } from './pages/Settings/pages/TeamRolesPage';
import { BookingPageSettingsPage } from './pages/Settings/pages/BookingPageSettingsPage';
import { ScheduleSettingsPage } from './pages/Settings/pages/ScheduleSettingsPage';
import { ServicesPricingPage } from './pages/Settings/pages/ServicesPricingPage';
import { ClientFieldsPage } from './pages/Settings/pages/ClientFieldsPage';
import { PaymentSetupPage } from './pages/Settings/pages/PaymentSetupPage';
import { TaxCompliancePage } from './pages/Settings/pages/TaxCompliancePage';
import { SubscriptionPage } from './pages/Settings/pages/SubscriptionPage';
import { NotificationsPage } from './pages/Settings/pages/NotificationsPage';
import { MarketingPage } from './pages/Settings/pages/MarketingPage';
import { LanguagePage } from './pages/Settings/pages/LanguagePage';
import { SyncIssuesPage } from './pages/Settings/pages/SyncIssuesPage';
import { AIFeaturesPage } from './pages/Settings/pages/AIFeaturesPage';
import { AutoFeaturesPage } from './pages/Settings/pages/AutoFeaturesPage';
import { MileageReportPage } from './pages/Reports/MileageReportPage';
import { DirectoryListingPage } from './pages/Settings/pages/DirectoryListingPage';
import { EmbedWidgetPage } from './pages/Settings/pages/EmbedWidgetPage';
import { PromoCodesPage } from './pages/PromoCodes';
import { DataPrivacyPage } from './pages/Settings/pages/DataPrivacyPage';
import { ImportConfigPage } from './pages/Settings/pages/ImportConfigPage';
import { ExportConfigPage } from './pages/Settings/pages/ExportConfigPage';
import { FleetPage } from './pages/Fleet';
import { JobsPage } from './pages/Jobs';
import { BookingsPage } from './pages/Bookings';
import { ProjectsPage } from './pages/Projects';
import { LocationsPage } from './pages/Locations';
import { ServicesPage } from './pages/Services';
import { ClientsPage } from './pages/Clients';
import { VendorsPage } from './pages/Vendors';
import { DispatchPage } from './pages/Dispatch';
import { KioskPage } from './pages/Kiosk/KioskPage';
import { ConfirmBookingPage } from './pages/ConfirmBooking';
import { IntakeFormPage } from './pages/IntakeForm';
import { CheckInPage } from './pages/CheckIn';
import { CrewPortalPage } from './pages/CrewPortal/CrewPortalPage';
// Phase F (§T.115): legacy quote/change-order/invoice portals retired —
// replaced by hosted-page-render EF at /{slug}/q/{token}, /{slug}/co/{token},
// /{slug}/pay/{token}. New sends emit slug-aware URLs from mobile SendQuoteSheet (F-U1).
import { ConnectCompletePage } from './pages/ConnectComplete';
import { ConnectRefreshPage } from './pages/ConnectRefresh';
import { palette, fontFamily } from './styles/tokens';

function AuthGate({ children }: { children: React.ReactNode }) {
  const session = useAuthStore(s => s.session);
  const user = useAuthStore(s => s.user);
  const business = useAuthStore(s => s.business);
  const loading = useAuthStore(s => s.loading);

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: palette.bg, fontFamily,
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, margin: '0 auto 12px',
            background: `linear-gradient(135deg, ${palette.accent}, ${palette.accentDeep})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
          </div>
          <div style={{ fontSize: 14, color: palette.g400, marginTop: 8 }}>Loading...</div>
        </div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (!user || !user.business_id) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: palette.bg, fontFamily,
      }}>
        <div style={{
          textAlign: 'center', padding: '40px',
          background: palette.white, borderRadius: 10, border: `1px solid ${palette.g150}`,
          maxWidth: 400,
        }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: palette.g800, marginBottom: 8 }}>
            No business found
          </div>
          <div style={{ fontSize: 14, color: palette.g500 }}>
            Download the CrewBit app to get started. Once you create a business, you can manage it here.
          </div>
        </div>
      </div>
    );
  }

  if (user.role !== 'owner' && user.role !== 'manager') {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: palette.bg, fontFamily,
      }}>
        <div style={{
          textAlign: 'center', padding: '40px',
          background: palette.white, borderRadius: 10, border: `1px solid ${palette.g150}`,
          maxWidth: 400,
        }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: palette.g800, marginBottom: 8 }}>
            Access restricted
          </div>
          <div style={{ fontSize: 14, color: palette.g500 }}>
            The Admin Panel is available for owners and managers. Contact your business owner for access.
          </div>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: palette.bg, fontFamily,
      }}>
        <div style={{
          textAlign: 'center', padding: '40px',
          background: palette.white, borderRadius: 10, border: `1px solid ${palette.g150}`,
          maxWidth: 420,
        }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: palette.g800, marginBottom: 8 }}>
            Couldn&apos;t load your business
          </div>
          <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: 20 }}>
            We hit an error fetching your business profile. This is usually temporary — try refreshing. If it keeps happening, sign out and back in.
          </div>
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 24px', borderRadius: 8, border: 'none',
              background: palette.accent, color: palette.white,
              fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            Refresh
          </button>
        </div>
        <div style={{ marginTop: 20, fontSize: 13, color: palette.g500 }}>
          <button
            type="button"
            onClick={() => { void useAuthStore.getState().signOut(); }}
            style={{
              background: 'transparent', border: 'none', padding: 0,
              color: palette.g500, fontSize: 13, fontFamily, cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  // Tier gate: Business tier or active trial required
  const trialActive = business.trial_ends_at ? new Date(business.trial_ends_at) > new Date() : false;
  const hasAdminAccess = business.tier === 'business' || trialActive;
  if (!hasAdminAccess) {
    return (
      <div style={{
        minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        background: palette.bg, fontFamily,
      }}>
        <div style={{
          textAlign: 'center', padding: '40px',
          background: palette.white, borderRadius: 10, border: `1px solid ${palette.g150}`,
          maxWidth: 420,
        }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, margin: '0 auto 16px',
            background: `linear-gradient(135deg, ${palette.accent}, ${palette.accentDeep})`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
          </div>
          <div style={{ fontSize: 18, fontWeight: 600, color: palette.g800, marginBottom: 8 }}>
            Upgrade to Business
          </div>
          <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5 }}>
            The admin panel is available on the Business plan. Your 14-day trial has ended. Upgrade to keep your desktop command center.
          </div>
          <a href="https://crewbit.ca/pricing" target="_blank" rel="noopener noreferrer" style={{
            display: 'inline-block', marginTop: 20, padding: '10px 24px', borderRadius: 8,
            background: palette.accent, color: '#fff', fontSize: 14, fontWeight: 600,
            textDecoration: 'none',
          }}>
            View Plans
          </a>
        </div>
        <div style={{ marginTop: 20, fontSize: 13, color: palette.g500 }}>
          <button
            type="button"
            onClick={() => { void useAuthStore.getState().signOut(); }}
            style={{
              background: 'transparent', border: 'none', padding: 0,
              color: palette.g500, fontSize: 13, fontFamily, cursor: 'pointer',
              textDecoration: 'underline',
            }}
          >
            Sign out
          </button>
          <span style={{ margin: '0 8px', color: palette.g300 }}>·</span>
          <a href="https://crewbit.ca" style={{ color: palette.g500, textDecoration: 'underline' }}>
            Back to crewbit.ca
          </a>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

export default function App() {
  const initialize = useAuthStore(s => s.initialize);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  const session = useAuthStore(s => s.session);
  const user = useAuthStore(s => s.user);
  const business = useAuthStore(s => s.business);

  // Active-business hydrate (Money Module Phase B.2). Resolves memberships,
  // auto-picks for single-membership users, surfaces requiresSelection for
  // multi-membership. Spec: docs/references/MONEY_MODULE_WIRING_v1.md §3.10.
  useEffect(() => {
    if (session?.user) {
      void useActiveBusinessStore.getState().hydrate();
    } else {
      useActiveBusinessStore.getState().reset();
    }
  }, [session]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* Public routes — no auth, no sidebar */}
          {/* Decision #588: app.crewbit.ca is the canonical host for ALL public surfaces.
              /book|/connect|/invite are PROXIED to the src storefront deployment via
              vercel.json rewrites; the walk-in counter-tablet kiosk lives at /kiosk. */}
          <Route path="/kiosk/:slug" element={<KioskPage />} />
          <Route path="/confirm-booking/:bookingId" element={<ConfirmBookingPage />} />
          <Route path="/intake/:businessId/:clientId" element={<IntakeFormPage />} />
          <Route path="/checkin/:slug" element={<CheckInPage />} />
          <Route path="/crew/:slug" element={<CrewPortalPage />} />
          <Route path="/crew/preview/:userId" element={<CrewPortalPage mode="preview" />} />
          {/* Phase F (§T.115): /quote/:token, /change-order/:token, /invoice/:token
              retired — hosted-page-render EF serves these from /{slug}/{route}/{token}. */}
          <Route path="/connect-complete" element={<ConnectCompletePage />} />
          <Route path="/connect-refresh" element={<ConnectRefreshPage />} />

          <Route path="/login" element={
            session && user?.business_id && (user.role === 'owner' || user.role === 'manager')
              ? <Navigate to="/today" replace />
              : <LoginPage />
          } />

          <Route path="/" element={
            <AuthGate>
              <AppShell business={business!} user={user!} />
            </AuthGate>
          }>
            <Route index element={<Navigate to="/today" replace />} />
            <Route path="today" element={<ErrorBoundary><DashboardRouter /></ErrorBoundary>} />
            <Route path="dashboard" element={<Navigate to="/today" replace />} />
            <Route path="dashboard/*" element={<Navigate to="/today" replace />} />
            <Route path="setup" element={<ErrorBoundary><SetupGuidePage /></ErrorBoundary>} />
            <Route path="schedule" element={<ErrorBoundary><SchedulePage /></ErrorBoundary>} />
            <Route path="team" element={<ErrorBoundary><TeamPage /></ErrorBoundary>} />
            <Route path="money" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneyOverviewAdmin /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/outstanding" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneyOutstandingAdmin /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/activity" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneyActivityPage /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/expenses" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneyExpensesPage /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/invoices" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneyInvoicesPage /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/tax" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneyTaxPage /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/disputes" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneyDisputesPage /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/recurring" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneyRecurringPage /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/revenue" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneyRevenuePage /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/settings" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneySettingsLanding /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/settings/paid" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneySettingsPaid /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/settings/taxes" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneySettingsTaxes /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/settings/people" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneySettingsPeople /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/settings/notifications" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneySettingsNotifications /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="money/settings/branding" element={<ErrorBoundary><RequiresActiveBusinessGate><MoneySettingsBranding /></RequiresActiveBusinessGate></ErrorBoundary>} />
            <Route path="invoices" element={<Navigate to="/money/invoices" replace />} />
            <Route path="jobs" element={<ErrorBoundary><JobsPage /></ErrorBoundary>} />
            <Route path="bookings" element={<ErrorBoundary><BookingsPage /></ErrorBoundary>} />
            <Route path="projects" element={<ErrorBoundary><ProjectsPage /></ErrorBoundary>} />
            <Route path="locations" element={<ErrorBoundary><LocationsPage /></ErrorBoundary>} />
            <Route path="services" element={<ErrorBoundary><ServicesPage /></ErrorBoundary>} />
            <Route path="clients" element={<ErrorBoundary><ClientsPage /></ErrorBoundary>} />
            <Route path="vendors" element={<ErrorBoundary><VendorsPage /></ErrorBoundary>} />
            <Route path="messages" element={<ErrorBoundary><MessagesPage /></ErrorBoundary>} />
            <Route path="moderation" element={<ErrorBoundary><PlatformAdminGate><ModerationPage /></PlatformAdminGate></ErrorBoundary>} />
            <Route path="dispatch" element={<ErrorBoundary><DispatchPage /></ErrorBoundary>} />
            <Route path="fleet" element={<ErrorBoundary><FleetPage /></ErrorBoundary>} />
            <Route path="reports" element={<ErrorBoundary><ReportsPage /></ErrorBoundary>} />
            <Route path="reports/mileage" element={<ErrorBoundary><MileageReportPage /></ErrorBoundary>} />
            <Route path="settings" element={<ErrorBoundary><SettingsLayout /></ErrorBoundary>}>
              <Route index element={<SettingsHub />} />
              <Route path="profile" element={<BusinessProfilePage />} />
              <Route path="hours" element={<BusinessHoursPage />} />
              <Route path="team" element={<TeamRolesPage />} />
              <Route path="booking" element={<BookingPageSettingsPage />} />
              <Route path="schedule" element={<ScheduleSettingsPage />} />
              <Route path="services" element={<ServicesPricingPage />} />
              <Route path="fields" element={<ClientFieldsPage />} />
              <Route path="payments" element={<PaymentSetupPage />} />
              <Route path="invoicing" element={<Navigate to="/money/settings/branding" replace />} />
              <Route path="tax" element={<TaxCompliancePage />} />
              <Route path="subscription" element={<SubscriptionPage />} />
              <Route path="notifications" element={<NotificationsPage />} />
              <Route path="marketing" element={<MarketingPage />} />
              <Route path="language" element={<LanguagePage />} />
              <Route path="ai" element={<AIFeaturesPage />} />
              <Route path="auto-features" element={<AutoFeaturesPage />} />
              <Route path="directory" element={<DirectoryListingPage />} />
              <Route path="embed" element={<EmbedWidgetPage />} />
              <Route path="data" element={<DataPrivacyPage />} />
              <Route path="import" element={<ImportConfigPage />} />
              <Route path="export" element={<ExportConfigPage />} />
              <Route path="promos" element={<PromoCodesPage />} />
              <Route path="sync-issues" element={<SyncIssuesPage />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/today" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
